import type { VoiceMessage } from '@orborix/voice-client';
import type { CorrectionLevel } from '@orborix/shared-types';
import type { TranscriptCorrection } from '../../lib/sessionsApi.js';

/**
 * Normalized inline-error descriptor consumed by `annotateText` + `ErrorDetail`.
 * Built from the real WS data on a user `VoiceMessage`:
 *   - grammar  ← `message.corrections` (`VoiceMessageCorrection[]`).
 *   - phonetic ← `message.pronunciationCorrections` (`PronunciationCorrection[]`).
 *
 * The design used a richer shape (syllables, etc.); we degrade gracefully to the
 * fields the contract actually exposes. Phonetic errors require Pronunciation
 * Assessment to be enabled in the backend (`SPEECHACE_API_KEY` or
 * `PRONUNCIATION_PROVIDER=phonos`) — when absent the backend simply emits no
 * `pronunciationCorrections` and no amber underlines appear.
 */
export type ConvErrorKind = 'grammar' | 'phonetic';

/**
 * Severidad de una corrección de gramática. Define el color/estilo del
 * subrayado y de la card, y el filtro por sensibilidad (Fase B). `suggestion`
 * = NO es error (más natural / upgrade). Orden de gravedad descendente:
 * communication-breaking > structural > minor > suggestion. Espejo del enum del
 * backend (`CorrectionSeverity`). Para `phonetic` no aplica (default
 * `structural`, no se usa: el render fonético tiene su propio color ámbar).
 */
export type ConvErrorSeverity =
  | 'communication-breaking'
  | 'structural'
  | 'minor'
  | 'suggestion';

/** Normaliza un severity de origen desconocido. Default `structural` (fail-safe). */
export function toConvSeverity(s: string | null | undefined): ConvErrorSeverity {
  return s === 'communication-breaking' || s === 'minor' || s === 'suggestion'
    ? s
    : 'structural';
}

/** Rango de gravedad (mayor = más grave). */
const SEVERITY_RANK: Record<ConvErrorSeverity, number> = {
  'communication-breaking': 3,
  structural: 2,
  minor: 1,
  suggestion: 0,
};

/** Rango mínimo de gravedad que cada nivel de sensibilidad deja pasar. */
const LEVEL_MIN_RANK: Record<CorrectionLevel, number> = {
  essential: 2, // solo structural + communication-breaking
  balanced: 1, // + minor
  all: 0, // + suggestion
};

/**
 * `true` si la corrección se muestra al nivel de sensibilidad elegido. Las
 * fonéticas siempre pasan (tienen su propio toggle "Análisis fonético"); el
 * filtro de severidad aplica solo a gramática (Fase 14B).
 */
export function isErrorVisibleAtLevel(err: ConvError, level: CorrectionLevel): boolean {
  if (err.kind !== 'grammar') return true;
  return SEVERITY_RANK[err.severity] >= LEVEL_MIN_RANK[level];
}

/** Filtra una lista de errores por el nivel de sensibilidad de gramática. */
export function filterErrorsByLevel(
  errors: ReadonlyArray<ConvError>,
  level: CorrectionLevel,
): ConvError[] {
  return errors.filter((e) => isErrorVisibleAtLevel(e, level));
}

export interface ConvSyllable {
  text: string;
  ipa: string;
  stress: 'primary' | 'secondary' | 'none';
}

export interface ConvError {
  id: string;
  kind: ConvErrorKind;
  /** Substring of the user text to underline. */
  wrong: string;
  /** Grammar: the corrected phrase. */
  right: string;
  /**
   * Grammar: severidad clasificada por el modelo (color + filtro por
   * sensibilidad). Para `phonetic` queda en `structural` y no se usa.
   */
  severity: ConvErrorSeverity;
  /** Phonetic: the word being corrected (display headline). */
  word: string;
  /** Phonetic: what the user said in IPA. */
  yours: string;
  /** Phonetic: target IPA / grammar has none. */
  ipa: string;
  /** Explanation ("why" for grammar / "tip" for phonetic). */
  note: string;
  /**
   * Phonetic: desglose silábico (chips del drill). Opcional — si el backend no
   * lo provee, el DrillModal omite los chips (no bloqueante). Ver plan §F.1.
   */
  syllables?: ConvSyllable[];
  /**
   * Grammar: índice del turn al que pertenece la corrección persistida. Parte
   * de la clave natural del voto 👍/👎 (plan 26 §7.4). `null` = persistida sin
   * turno; `undefined` = el origen no lo informó → los thumbs no se ofrecen.
   */
  turnIndex?: number | null;
  /** Grammar: voto previo del usuario (rehidratado del historial/summary). */
  feedback?: { vote: 'up' | 'down'; reason?: string };
  /**
   * Grammar: el coach DIJO esta corrección en voz alta (coda del modo manos
   * libres). Badge informativo en `ErrorDetail`; sólo en vivo (no persiste).
   */
  spoken?: boolean;
}

export function buildErrors(message: VoiceMessage): ReadonlyArray<ConvError> {
  if (message.who !== 'user') return [];
  const out: ConvError[] = [];

  for (const [i, c] of (message.corrections ?? []).entries()) {
    out.push({
      id: `grammar:${c.said}:${i}`,
      kind: 'grammar',
      wrong: c.said,
      right: c.fix,
      severity: toConvSeverity(c.severity),
      word: c.said,
      yours: '',
      ipa: '',
      note: c.explanation ?? '',
      ...(c.turnIndex !== undefined ? { turnIndex: c.turnIndex } : {}),
      ...(c.spoken ? { spoken: true } : {}),
    });
  }

  for (const [i, pc] of (message.pronunciationCorrections ?? []).entries()) {
    out.push({
      id: `phonetic:${pc.word}:${i}`,
      kind: 'phonetic',
      wrong: pc.word,
      right: pc.word,
      severity: 'structural',
      word: pc.word,
      yours: pc.saidIpa,
      ipa: pc.targetIpa,
      note: pc.tip || pc.detail || '',
    });
  }

  return out;
}

/**
 * Misma normalización que `buildErrors`, pero desde las correcciones
 * persistidas que devuelve el endpoint `/sessions/:id/transcript`
 * (`TranscriptCorrection`). Permite que el historial reuse `UserBubble` +
 * `ErrorDetail` en vez de duplicar tarjetas propias. El backend ya asigna un
 * `id` único por corrección, así que lo conservamos (la navegación/ciclado de
 * `UserBubble` necesita ids estables y únicos).
 */
export function buildErrorsFromTranscript(
  corrections: ReadonlyArray<TranscriptCorrection>,
  /** Turno al que pertenecen (lo conoce el caller — clave del voto 👍/👎). */
  turnIndex?: number,
): ReadonlyArray<ConvError> {
  return corrections.map((c) =>
    c.kind === 'grammar'
      ? {
          id: c.id,
          kind: 'grammar' as const,
          wrong: c.said,
          right: c.fix,
          severity: toConvSeverity(c.severity),
          word: c.said,
          yours: '',
          ipa: '',
          note: c.explanation ?? '',
          ...(turnIndex !== undefined ? { turnIndex } : {}),
          ...(c.feedback ? { feedback: c.feedback } : {}),
        }
      : {
          id: c.id,
          kind: 'phonetic' as const,
          wrong: c.word,
          right: c.word,
          severity: 'structural' as const,
          word: c.word,
          yours: c.saidIpa ?? '',
          ipa: c.targetIpa ?? '',
          note: c.tip ?? '',
        },
  );
}
